/**
 * PostService - Service Layer for Post operations
 *
 * This service encapsulates all Firebase/Firestore operations for posts,
 * providing a clean interface for hooks and components to consume.
 *
 * Design Patterns Used:
 * - Service Layer Pattern: Encapsulates business logic and data access
 * - Facade Pattern: Simplifies complex Firebase operations
 * - Adapter Pattern: Converts between Firebase and domain types
 */

import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  QueryConstraint,
  startAfter,
  DocumentSnapshot,
  getCountFromServer,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { AppError, NotFoundError, ValidationError, fromFirebaseError } from '../errors';
import { COLLECTIONS } from '../constants';
import type { Post, ContentType, PostStatus, ContentBlock } from '../types';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface PostFilters {
  type?: ContentType;
  status?: PostStatus;
  authorId?: string;
  tags?: string[];
}

export interface PostQueryOptions {
  limit?: number;
  orderByField?: 'updatedAt' | 'createdAt' | 'publishedAt';
  orderDirection?: 'asc' | 'desc';
}

export interface PaginationOptions extends PostQueryOptions {
  cursor?: DocumentSnapshot | null;
}

export interface PaginatedResult<T> {
  data: T[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}

export interface CreatePostInput {
  type: ContentType;
  title: string;
  slug: string;
  description: string;
  content: ContentBlock[];
  thumbnail: string;
  thumbnailMediaId?: string;
  thumbnailSrcSet?: string;
  tags: string[];
  client?: string;
  location?: string;
  date?: Date;
  status: PostStatus;
  authorId: string;
}

export interface UpdatePostInput {
  title?: string;
  slug?: string;
  description?: string;
  content?: ContentBlock[];
  thumbnail?: string;
  thumbnailMediaId?: string;
  thumbnailSrcSet?: string;
  tags?: string[];
  client?: string;
  location?: string;
  date?: Date | null;
  status?: PostStatus;
  publishedAt?: Date | null;
}

// =============================================================================
// DOCUMENT MAPPER (Adapter Pattern)
// =============================================================================

/**
 * Converts a Firestore document to a Post domain object.
 * Handles timestamp conversion and data normalization.
 */
function documentToPost(docSnap: DocumentSnapshot): Post {
  const data = docSnap.data();
  if (!data) {
    throw new NotFoundError('Post', docSnap.id);
  }

  return {
    id: docSnap.id,
    type: data.type,
    title: data.title,
    slug: data.slug,
    description: data.description,
    content: data.content || [],
    thumbnail: data.thumbnail,
    thumbnailMediaId: data.thumbnailMediaId || undefined,
    thumbnailSrcSet: data.thumbnailSrcSet || undefined,
    tags: data.tags || [],
    client: data.client || undefined,
    location: data.location || undefined,
    date: data.date?.toDate?.() || null,
    status: data.status,
    authorId: data.authorId,
    createdAt: data.createdAt?.toDate?.() || new Date(),
    updatedAt: data.updatedAt?.toDate?.() || new Date(),
    publishedAt: data.publishedAt?.toDate?.() || null,
  } as Post;
}

/**
 * Converts a CreatePostInput to Firestore document format.
 */
function createInputToDocument(input: CreatePostInput): Record<string, unknown> {
  return {
    type: input.type,
    title: input.title,
    slug: input.slug,
    description: input.description,
    content: input.content,
    thumbnail: input.thumbnail,
    thumbnailMediaId: input.thumbnailMediaId || null,
    thumbnailSrcSet: input.thumbnailSrcSet || null,
    tags: input.tags,
    client: input.client || null,
    location: input.location || null,
    date: input.date ? Timestamp.fromDate(input.date) : null,
    status: input.status,
    authorId: input.authorId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    publishedAt: input.status === 'published' ? serverTimestamp() : null,
  };
}

/**
 * Converts an UpdatePostInput to Firestore update format.
 */
function updateInputToDocument(input: UpdatePostInput): Record<string, unknown> {
  const updateData: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (input.title !== undefined) updateData.title = input.title;
  if (input.slug !== undefined) updateData.slug = input.slug;
  if (input.description !== undefined) updateData.description = input.description;
  if (input.content !== undefined) updateData.content = input.content;
  if (input.thumbnail !== undefined) updateData.thumbnail = input.thumbnail;
  if (input.thumbnailMediaId !== undefined) updateData.thumbnailMediaId = input.thumbnailMediaId || null;
  if (input.thumbnailSrcSet !== undefined) updateData.thumbnailSrcSet = input.thumbnailSrcSet || null;
  if (input.tags !== undefined) updateData.tags = input.tags;
  if (input.client !== undefined) updateData.client = input.client || null;
  if (input.location !== undefined) updateData.location = input.location || null;
  if (input.status !== undefined) updateData.status = input.status;

  if (input.date !== undefined) {
    updateData.date = input.date ? Timestamp.fromDate(input.date) : null;
  }

  if (input.publishedAt !== undefined) {
    updateData.publishedAt = input.publishedAt
      ? Timestamp.fromDate(input.publishedAt)
      : null;
  }

  return updateData;
}

// =============================================================================
// QUERY BUILDER (Builder Pattern)
// =============================================================================

/**
 * Builds Firestore query constraints from filters and options.
 */
function buildQueryConstraints(
  filters: PostFilters,
  options: PostQueryOptions
): QueryConstraint[] {
  const constraints: QueryConstraint[] = [];

  if (filters.type) {
    constraints.push(where('type', '==', filters.type));
  }

  if (filters.status) {
    constraints.push(where('status', '==', filters.status));
  }

  if (filters.authorId) {
    constraints.push(where('authorId', '==', filters.authorId));
  }

  // Firestore only supports one array-contains per query
  if (filters.tags && filters.tags.length > 0) {
    constraints.push(where('tags', 'array-contains', filters.tags[0]));
  }

  const orderField = options.orderByField || 'updatedAt';
  const orderDir = options.orderDirection || 'desc';
  constraints.push(orderBy(orderField, orderDir));

  if (options.limit) {
    constraints.push(limit(options.limit));
  }

  return constraints;
}

/**
 * Filters posts by multiple tags (client-side filtering).
 * Firestore limitation: only one array-contains per query.
 */
function filterByMultipleTags(posts: Post[], tags: string[]): Post[] {
  if (tags.length <= 1) return posts;
  return posts.filter((post) => tags.every((tag) => post.tags.includes(tag)));
}

// =============================================================================
// POST SERVICE (Service Layer Pattern)
// =============================================================================

/**
 * PostService provides a high-level interface for post operations.
 * All Firebase implementation details are encapsulated within this service.
 */
export const PostService = {
  /**
   * Fetches multiple posts with optional filtering.
   */
  async findMany(
    filters: PostFilters = {},
    options: PostQueryOptions = {}
  ): Promise<Post[]> {
    try {
      const constraints = buildQueryConstraints(filters, options);
      const q = query(collection(db, COLLECTIONS.posts), ...constraints);
      const snapshot = await getDocs(q);

      const posts = snapshot.docs.map(documentToPost);
      return filterByMultipleTags(posts, filters.tags || []);
    } catch (error) {
      throw fromFirebaseError(error);
    }
  },

  /**
   * Fetches posts with pagination support.
   */
  async findManyPaginated(
    filters: PostFilters = {},
    options: PaginationOptions = {}
  ): Promise<PaginatedResult<Post>> {
    try {
      const pageSize = options.limit || 20;
      const constraints = buildQueryConstraints(filters, { ...options, limit: pageSize });

      if (options.cursor) {
        constraints.push(startAfter(options.cursor));
      }

      const q = query(collection(db, COLLECTIONS.posts), ...constraints);
      const snapshot = await getDocs(q);

      const posts = snapshot.docs.map(documentToPost);
      const filteredPosts = filterByMultipleTags(posts, filters.tags || []);
      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;

      return {
        data: filteredPosts,
        lastDoc,
        hasMore: snapshot.docs.length === pageSize,
      };
    } catch (error) {
      throw fromFirebaseError(error);
    }
  },

  /**
   * Gets the total count of posts matching the filters.
   */
  async count(filters: PostFilters = {}): Promise<number> {
    try {
      const constraints = buildQueryConstraints(filters, {});
      // Remove limit constraint for count
      const countConstraints = constraints.filter(
        (c) => !(c as unknown as { type: string }).type?.includes('limit')
      );
      const countQuery = query(collection(db, COLLECTIONS.posts), ...countConstraints);
      const snapshot = await getCountFromServer(countQuery);
      return snapshot.data().count;
    } catch (error) {
      throw fromFirebaseError(error);
    }
  },

  /**
   * Fetches a single post by ID.
   */
  async findById(postId: string): Promise<Post> {
    if (!postId) {
      throw new ValidationError({ postId: 'Post ID is required' });
    }

    try {
      const docRef = doc(db, COLLECTIONS.posts, postId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new NotFoundError('Post', postId);
      }

      return documentToPost(docSnap);
    } catch (error) {
      if (error instanceof AppError) throw error;
      throw fromFirebaseError(error);
    }
  },

  /**
   * Fetches a post by its slug.
   * @param slug - The post slug to search for
   * @param status - Optional status filter (required for unauthenticated users due to security rules)
   */
  async findBySlug(slug: string, status?: PostStatus): Promise<Post | null> {
    if (!slug) {
      throw new ValidationError({ slug: 'Slug is required' });
    }

    try {
      const constraints: QueryConstraint[] = [
        where('slug', '==', slug),
        limit(1),
      ];

      // Add status filter if provided (required for public/unauthenticated access)
      if (status) {
        constraints.unshift(where('status', '==', status));
      }

      const q = query(collection(db, COLLECTIONS.posts), ...constraints);
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      return documentToPost(snapshot.docs[0]);
    } catch (error) {
      throw fromFirebaseError(error);
    }
  },

  /**
   * Creates a new post.
   */
  async create(input: CreatePostInput): Promise<string> {
    // Validate required fields
    if (!input.title?.trim()) {
      throw new ValidationError({ title: 'Title is required' });
    }
    if (!input.slug?.trim()) {
      throw new ValidationError({ slug: 'Slug is required' });
    }

    try {
      // Check for duplicate slug
      const existing = await PostService.findBySlug(input.slug);
      if (existing) {
        throw new ValidationError(
          { slug: 'A post with this slug already exists' },
          '이 슬러그는 이미 사용 중입니다.'
        );
      }

      const docData = createInputToDocument(input);
      const docRef = await addDoc(collection(db, COLLECTIONS.posts), docData);
      return docRef.id;
    } catch (error) {
      if (error instanceof AppError) throw error;
      throw fromFirebaseError(error);
    }
  },

  /**
   * Updates an existing post.
   */
  async update(postId: string, input: UpdatePostInput): Promise<void> {
    if (!postId) {
      throw new ValidationError({ postId: 'Post ID is required' });
    }

    try {
      // Verify post exists
      await PostService.findById(postId);

      // Check for duplicate slug if slug is being updated
      if (input.slug) {
        const existing = await PostService.findBySlug(input.slug);
        if (existing && existing.id !== postId) {
          throw new ValidationError(
            { slug: 'A post with this slug already exists' },
            '이 슬러그는 이미 사용 중입니다.'
          );
        }
      }

      const docRef = doc(db, COLLECTIONS.posts, postId);
      const updateData = updateInputToDocument(input);
      await updateDoc(docRef, updateData);
    } catch (error) {
      if (error instanceof AppError) throw error;
      throw fromFirebaseError(error);
    }
  },

  /**
   * Deletes a post.
   */
  async delete(postId: string): Promise<void> {
    if (!postId) {
      throw new ValidationError({ postId: 'Post ID is required' });
    }

    try {
      // Verify post exists
      await PostService.findById(postId);
      await deleteDoc(doc(db, COLLECTIONS.posts, postId));
    } catch (error) {
      if (error instanceof AppError) throw error;
      throw fromFirebaseError(error);
    }
  },

  /**
   * Publishes a post (sets status to 'published' and updates publishedAt).
   */
  async publish(postId: string): Promise<void> {
    await PostService.update(postId, {
      status: 'published',
      publishedAt: new Date(),
    });
  },

  /**
   * Unpublishes a post (sets status to 'draft' and clears publishedAt).
   */
  async unpublish(postId: string): Promise<void> {
    await PostService.update(postId, {
      status: 'draft',
      publishedAt: null,
    });
  },
};

